import React from 'react';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { getNewsLink } from '../../content/news';

export function NewsCategoryBadge({ category }) {
  // pt/pb の非対称は Noto Sans JP のメトリクスで文字が約1px下がるのを補正するため
  return (
    <span className="inline-flex min-w-16 items-center justify-center border-l-2 border-primary bg-primary-50 px-2.5 pb-[5px] pt-[3px] text-xs font-semibold leading-none text-foreground">
      {category.name}
    </span>
  );
}

// 日付とカテゴリバッジの組。一覧・詳細ヒーローで共通利用する
export function NewsMeta({ item, linkCategory = false, className = '' }) {
  const intl = useIntl();

  return (
    <div
      className={`flex flex-wrap items-baseline gap-x-4 gap-y-2 ${className}`}
    >
      <time
        dateTime={item.releaseDate}
        className="font-heading text-xs font-medium tracking-date text-muted-foreground sm:text-sm"
      >
        {intl.formatDate(item.releaseDate, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })}
      </time>
      {linkCategory ? (
        <Link
          to={`/news/?category=${encodeURIComponent(item.category.slug)}`}
          className="transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4"
        >
          <NewsCategoryBadge category={item.category} />
        </Link>
      ) : (
        <NewsCategoryBadge category={item.category} />
      )}
    </div>
  );
}

export default function NewsListItem({
  item,
  showThumbnail = true,
  compact = false,
  headingLevel = 'h2',
}) {
  const Heading = headingLevel;
  const intl = useIntl();
  const destination = getNewsLink(item, intl.locale);
  const ItemLink = item.isExternal ? 'a' : Link;
  const linkProps = item.isExternal
    ? { href: destination.href, target: '_blank', rel: 'noopener noreferrer' }
    : { to: destination.href };

  return (
    <li className="border-t border-border last:border-b">
      <ItemLink
        {...linkProps}
        lang={destination.lang}
        className={`group flex items-start gap-4 transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring motion-reduce:transition-none sm:gap-6 ${
          compact ? 'py-5' : 'py-7 sm:py-8'
        }`}
      >
        <div className="min-w-0 flex-1">
          <NewsMeta item={item} className="mb-3" />
          <Heading
            className={`min-w-0 text-pretty font-body text-foreground transition-colors [overflow-wrap:anywhere] motion-reduce:transition-none ${
              compact
                ? 'text-base font-normal leading-[1.8] underline-offset-4 group-hover:underline'
                : 'line-clamp-3 text-base font-medium leading-[1.8] group-hover:text-primary sm:text-lg'
            }`}
          >
            {destination.title}
          </Heading>
        </div>

        {showThumbnail && item.thumbnail && (
          <div className="relative hidden aspect-[40/21] w-40 shrink-0 overflow-hidden rounded-lg bg-muted sm:block lg:w-52">
            <img
              src={item.thumbnail.url}
              width={item.thumbnail.width}
              height={item.thumbnail.height}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105 motion-reduce:transform-none motion-reduce:transition-none"
            />
            <span className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-foreground opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </div>
        )}

        <ChevronRight
          className="h-5 w-5 shrink-0 self-center text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary motion-reduce:transform-none motion-reduce:transition-none"
          aria-hidden="true"
        />
        {item.isExternal && (
          <span className="sr-only">
            {intl.formatMessage({ id: 'news.opensInNewTab' })}
          </span>
        )}
      </ItemLink>
    </li>
  );
}
